import type {
  TProcessingAction,
  TPublisherLyricsApprovalDraftReviewSong,
  TPublisherRequest,
} from '@modules/types'

export type ApprovalLyricsState = {
  editingState: Record<string, boolean>
  feeValues: Record<string, string>
  processingSongId: number | null
  processingAction: TProcessingAction
}

export type ApprovalLyricsAction =
  | { type: 'SYNC_FROM_REQUEST'; payload: { selectedRequest?: TPublisherRequest } }
  | { type: 'SET_EDITING'; payload: { key: string; value: boolean } }
  | { type: 'SET_VALUE'; payload: { key: string; value: string } }
  | { type: 'SET_PROCESSING'; payload: { songId: number; action: TProcessingAction } }
  | { type: 'CLEAR_PROCESSING' }

export const initialApprovalLyricsState: ApprovalLyricsState = {
  editingState: {},
  feeValues: {},
  processingSongId: null,
  processingAction: null,
}

export const approvalLyricsReducer = (
  state: ApprovalLyricsState,
  action: ApprovalLyricsAction,
): ApprovalLyricsState => {
  switch (action.type) {
    case 'SYNC_FROM_REQUEST': {
      const { selectedRequest } = action.payload
      if (!selectedRequest?.draftReview?.songs) {
        return initialApprovalLyricsState
      }

      const { songs } = selectedRequest.draftReview
      const feeValues: Record<string, string> = {}
      const validKeys = new Set<string>()
      songs.forEach(song => {
        const draftSong = song as TPublisherLyricsApprovalDraftReviewSong
        const value = draftSong.lyricsPublisherFee ?? draftSong.counterOfferFee
        if (value != null) {
          const key = String(song.songId)
          feeValues[key] = value.toString()
          validKeys.add(key)
        }
      })

      const editingState = Object.fromEntries(
        Object.entries(state.editingState).filter(([key]) => validKeys.has(key)),
      )

      return {
        ...state,
        editingState,
        feeValues,
      }
    }
    case 'SET_EDITING':
      return {
        ...state,
        editingState: {
          ...state.editingState,
          [action.payload.key]: action.payload.value,
        },
      }
    case 'SET_VALUE':
      return {
        ...state,
        feeValues: {
          ...state.feeValues,
          [action.payload.key]: action.payload.value,
        },
      }
    case 'SET_PROCESSING':
      return {
        ...state,
        processingSongId: action.payload.songId,
        processingAction: action.payload.action,
      }
    case 'CLEAR_PROCESSING':
      return {
        ...state,
        processingSongId: null,
        processingAction: null,
      }
    default:
      return state
  }
}
